"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CategoryIcon from "@mui/icons-material/Category"
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import InputAdornment from "@mui/material/InputAdornment"

const TablaSubCategoriaComponent = () => {
  const {
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    categoriaTransaccion,
    updateSubCategoriaTransaccion,
    deleteSubCategoriaTransaccion,
  } = useContext(ErpContext)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  
  // Estados para el modal de edición
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingSubcategoria, setEditingSubcategoria] = useState(null)
  const [editDescripcion, setEditDescripcion] = useState("")
  const [editIdCategoria, setEditIdCategoria] = useState("")

  useEffect(() => {
    if (getSubCategoriaTransaccion) {
      getSubCategoriaTransaccion()
    }
  }, [])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const getCategoriaDescripcion = (idCategoria) => {
    const categoria = categoriaTransaccion.find((cat) => cat.id == idCategoria)
    return categoria ? categoria.descripcion : "Sin categoría"
  }

  // Filtrar subcategorías basándose en el término de búsqueda
  const filteredData = subCategoriaTransaccion.filter((subcategoria) => {
    if (!searchTerm.trim()) return true
    
    const searchLower = searchTerm.toLowerCase()
    const descripcion = subcategoria.descripcion?.toLowerCase() || ""
    const categoriaDesc = getCategoriaDescripcion(subcategoria.idCategoriaTransaccion)?.toLowerCase() || ""
    const id = subcategoria.id?.toString() || ""
    
    return (
      descripcion.includes(searchLower) ||
      categoriaDesc.includes(searchLower) ||
      id.includes(searchLower)
    )
  })

  // Aplicar paginación a los datos filtrados
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // Resetear página cuando cambia el término de búsqueda
  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const handleOpenEditModal = (subcategoria) => {
    setActionError(null)
    setEditingSubcategoria(subcategoria)
    setEditDescripcion(subcategoria.descripcion)
    setEditIdCategoria(subcategoria.idCategoriaTransaccion)
    setEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setEditingSubcategoria(null)
    setEditDescripcion("")
    setEditIdCategoria("")
  }

  const handleSaveEdit = async () => {
    if (!editDescripcion.trim()) {
      setActionError("Por favor ingrese una descripción para la subcategoría")
      return
    }

    if (!editIdCategoria) {
      setActionError("Por favor seleccione una categoría principal")
      return
    }

    setActionLoadingId(editingSubcategoria.id)
    try {
      await updateSubCategoriaTransaccion(
        editingSubcategoria.id, 
        editDescripcion.trim(), 
        editIdCategoria
      )
      handleCloseEditModal()
      setActionError(null)
    } catch (error) {
      console.error("Error al actualizar subcategoría:", error)
      setActionError("No se pudo actualizar la subcategoría. Intente nuevamente.")
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleDeleteSubcategoria = async (idSubcategoria) => {
    const confirmDelete = window.confirm("¿Está seguro de eliminar esta subcategoría?")
    if (!confirmDelete) return

    setActionLoadingId(idSubcategoria)
    setActionError(null)
    try {
      await deleteSubCategoriaTransaccion(idSubcategoria)
    } catch (error) {
      console.error("Error al eliminar subcategoría:", error)
      
      // Verificar si el error indica que hay transacciones asociadas
      const errorMessage = error?.response?.data?.message || error?.message || "No se pudo eliminar la subcategoría"
      
      if (errorMessage.includes("transacción") || errorMessage.includes("transaccion")) {
        setActionError(`⚠️ ${errorMessage}. No se puede eliminar porque está en uso.`)
      } else {
        setActionError("No se pudo eliminar la subcategoría. Intente nuevamente.")
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  if (!subCategoriaTransaccion || subCategoriaTransaccion.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          px: 2,
        }}
      >
        <SubdirectoryArrowRightIcon
          sx={{
            fontSize: 64,
            color: alpha("rgb(244, 147, 14)", 0.3),
            mb: 2,
          }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No hay subcategorías registradas
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Las subcategorías que agregues aparecerán aquí
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {actionError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}
      
      {/* Buscador */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por subcategoría, categoría o ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#ffffff",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgb(244, 177, 14)" }} />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Mostrando {filteredData.length} de {subCategoriaTransaccion.length} subcategoría(s)
          </Typography>
        )}
      </Box>
      
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha("#000", 0.1)}`,
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="tabla de subcategorías">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#5b5b5f",
              }}
            >
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SubdirectoryArrowRightIcon sx={{ mr: 1, fontSize: 20 }} />
                  Subcategoría
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
                  Categoría Principal
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#ffffff" }}>ID</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#ffffff" }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((subcategoria, index) => (
              <TableRow
                key={subcategoria.id}
                sx={{
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD !important",
                    transition: "background 0.3s ease",
                  },
                  transition: "background 0.2s ease",
                }}
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {subcategoria.descripcion}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getCategoriaDescripcion(subcategoria.idCategoriaTransaccion)}
                    sx={{
                      backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      color: "rgb(244, 177, 14)",
                      fontWeight: 500,
                      "& .MuiChip-label": {
                        px: 2,
                      },
                    }}
                    icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={subcategoria.id}
                    size="small"
                    sx={{
                      backgroundColor: alpha("#5b5b5f", 0.1),
                      color: "#5b5b5f",
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <Tooltip title="Editar subcategoría">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditModal(subcategoria)}
                          disabled={actionLoadingId !== null}
                          sx={{
                            color: "rgb(244, 177, 14)",
                            bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                            "&:hover": {
                              bgcolor: alpha("rgb(244, 177, 14)", 0.2),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar subcategoría">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSubcategoria(subcategoria.id)}
                          disabled={actionLoadingId !== null}
                          sx={{
                            color: "#d32f2f",
                            bgcolor: alpha("#d32f2f", 0.1),
                            "&:hover": {
                              bgcolor: alpha("#d32f2f", 0.2),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          sx={{
            borderTop: `1px solid ${alpha("#000", 0.1)}`,
            "& .MuiTablePagination-toolbar": {
              px: 2,
            },
          }}
        />
      )}

      {/* Modal de Edición */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#5b5b5f",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditIcon />
          Editar Subcategoría
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Descripción de Subcategoría"
              variant="outlined"
              fullWidth
              value={editDescripcion}
              onChange={(e) => setEditDescripcion(e.target.value)}
              disabled={actionLoadingId !== null}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />
            
            <FormControl fullWidth variant="outlined">
              <InputLabel id="edit-categoria-label">Categoría Principal</InputLabel>
              <Select
                labelId="edit-categoria-label"
                value={editIdCategoria}
                onChange={(e) => setEditIdCategoria(e.target.value)}
                label="Categoría Principal"
                disabled={actionLoadingId !== null}
                sx={{
                  borderRadius: 2,
                }}
              >
                {categoriaTransaccion.map((categoria) => (
                  <MenuItem key={categoria.id} value={categoria.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CategoryIcon sx={{ fontSize: 18, color: "rgb(244, 177, 14)" }} />
                      {categoria.descripcion}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {actionError && (
              <Alert severity="error" onClose={() => setActionError(null)}>
                {actionError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseEditModal}
            disabled={actionLoadingId !== null}
            startIcon={<CloseIcon />}
            sx={{
              color: "#6b6b6b",
              "&:hover": {
                backgroundColor: alpha("#6b6b6b", 0.1),
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEdit}
            disabled={actionLoadingId !== null}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": {
                backgroundColor: alpha("#5b5b5f", 0.9),
              },
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TablaSubCategoriaComponent
